// Loki log shipper for the pingpong kiosk (DESIGN.md; knol/data/llm-docs/logging.md).
//
// Reuses the SAME /ifl relay app-vue2/app-v3 ship their warn/error console lines to, so kiosk
// issues land in the same Grafana streams as the rest of the platform
// ({job="frontend", app="pingpong-kiosk", level, domain}). Wire-compatible with app-vue2's
// src/ts/logShipper.ts on purpose: FrontendLogController.kt decompresses every event with
// decompressRrwebString, which expects zlib-deflated bytes decoded as a byte-preserving
// ("latin1") string — exactly what rrweb's pack() does. Pulling in the whole `rrweb` package
// just for that one function would be a lot of dead weight for a small kiosk app, so `pack()`
// is reimplemented here directly against `fflate` (the zero-dep library rrweb itself wraps).
//
// Scope is intentionally narrower than app-vue2's: only console.error/console.warn are patched,
// plus uncaught errors/rejections (installed from main.ts) — not every console.log/info/debug
// call. Shipping is best-effort and must NEVER break the kiosk or throw back into app code.
import { strFromU8, strToU8, zlibSync } from 'fflate'

// Round-22 (2026-08-31): 'info' added for shipEvent() — the "track everything" observability
// convention (DESIGN.md § Observability): recognition attempts/decisions/outcomes ship as
// structured 'info' lines, distinct from the warn/error console capture this file started as.
// The relay (FrontendLogController.kt) already accepts info/debug alongside warn/error.
export type ShipLevel = 'warn' | 'error' | 'info'

// Public, unauthenticated relay — same constant as server/app/config.py's KOMBO_API_BASE
// default. Not made configurable via a Vite env var: this is the only Kombo domain the kiosk
// will ever talk to for logging, and keeping it a plain constant avoids widening the small
// ImportMetaEnv surface (vite-env.d.ts) for a single hardcoded URL.
const IFL_ENDPOINT = 'https://api.kombo.so/api/v1/ifl'
const APP_TAG = 'pingpong-kiosk'
// Matches rrweb's packer/base.ts MARK — the backend ignores unknown fields, kept only so a
// packed line here is byte-for-byte what app-vue2/app-v3 would have produced for the same input.
const PACK_MARK = 'v1'

interface ShippedLogLine {
  level: ShipLevel
  scope: string
  msg: string
  ts: number
  pageUrl: string
  /** Hostname the kiosk is served from — a Loki LABEL (server-side), same convention as app-vue2. */
  domain: string
  /** Client-side aggregation: identical (level, scope, msg) repeats bump this instead of re-queueing. */
  count: number
  lastTs?: number
  data?: Record<string, unknown>
}

// Same budgets/spacing as app-vue2's ts/logShipper.ts: the kiosk page is meant to stay open for
// days (it's a wall-mounted display, never reloaded outside a deploy), the same "one page load
// runs for hours" reasoning that sized the courier app's budgets.
const FLUSH_INTERVAL_MS = 5000
const FLUSH_AT = 25
// Never POST more often than this — an error thrown on every render must aggregate into the
// queued line (count++), not become one request per frame.
const MIN_FLUSH_SPACING_MS = 2000
// Hard cap per page load for warn/error (the original, error-budget tier).
const MAX_PER_PAGE = 300
// Round-22: separate, more generous budget for 'info' track events (shipEvent) — a kiosk page
// stays open for days and every recognition attempt/decision now ships one line, which would
// exhaust the warn/error budget in minutes if they shared a counter. Kept apart so a busy day
// of legitimate track events can never crowd out real errors.
const MAX_INFO_PER_PAGE = 3000
// Per identical (level, scope, msg) line: after this many shipped lines per page load the key
// is muted (one 'error' marker line ships when the cap trips, so Loki shows it fired).
const MAX_PER_KEY_PER_PAGE = 20
const MAX_MSG_CHARS = 8000

let queue: ShippedLogLine[] = []
let shippedThisPage = 0
let infoShippedThisPage = 0
let flushTimer: ReturnType<typeof setInterval> | null = null
let flushing = false
let lastFlushAt = 0
let spacingTimer: ReturnType<typeof setTimeout> | null = null
const perKeyShipped = new Map<string, number>()

/** rrweb-pack()-equivalent: zlib-deflate the JSON line, decode as latin1 so every compressed
 * byte survives the round trip — what FrontendLogController.kt's decompressRrwebString expects. */
function pack(line: ShippedLogLine): string {
  const withMark = { ...line, v: PACK_MARK }
  return strFromU8(zlibSync(strToU8(JSON.stringify(withMark))), true)
}

/** Table id + any other kiosk query params, cheaply — DESIGN.md 9.3 (`?table=main&kiosk=1`). */
function currentQueryParams(): Record<string, string> {
  try {
    return Object.fromEntries(new URLSearchParams(window.location.search).entries())
  } catch {
    return {}
  }
}

function requestFlush() {
  const wait = lastFlushAt + MIN_FLUSH_SPACING_MS - Date.now()
  if (wait <= 0) {
    flush()
    return
  }
  if (!spacingTimer) {
    spacingTimer = setTimeout(() => {
      spacingTimer = null
      flush()
    }, wait)
  }
}

function flush(useKeepalive = false) {
  if (queue.length === 0 || flushing) return
  flushing = true
  lastFlushAt = Date.now()
  try {
    const lines = queue.splice(0, queue.length)
    const events = lines.map(pack)
    fetch(IFL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ app: APP_TAG, events }),
      // keepalive lets the pagehide flush survive navigation/reload.
      keepalive: useKeepalive,
    }).catch(() => {
      // Fire-and-forget by design: relay/network being down must never affect the kiosk.
    })
  } catch {
    // never let the shipper throw
  } finally {
    flushing = false
  }
}

function ensureTimer() {
  if (flushTimer) return
  flushTimer = setInterval(() => flush(), FLUSH_INTERVAL_MS)
  window.addEventListener('pagehide', () => flush(true))
}

/** Queue one warn/error line for Loki (aggregating identical repeats). Safe to call from anywhere. */
export function shipLog(level: ShipLevel, scope: string, args: unknown[]) {
  try {
    if (typeof window === 'undefined') return
    if (level === 'info') {
      if (infoShippedThisPage >= MAX_INFO_PER_PAGE) return
      infoShippedThisPage++
    } else {
      if (shippedThisPage >= MAX_PER_PAGE) return
      shippedThisPage++
    }
    ensureTimer()

    const msg = args
      .map((a) => {
        if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack ?? ''}`
        if (typeof a === 'string') return a
        try {
          return JSON.stringify(a)
        } catch {
          return String(a)
        }
      })
      .join(' ')
      .slice(0, MAX_MSG_CHARS)

    // `ts` keeps the FIRST occurrence; `lastTs` the latest — the window survives batching.
    const existing = queue.find((l) => l.level === level && l.scope === scope && l.msg === msg)
    if (existing) {
      existing.count++
      existing.lastTs = Date.now()
      return
    }

    // Per-key rate limit: a line that already shipped MAX_PER_KEY_PER_PAGE times this page
    // load is dropped, with one self-describing marker line so the drop is visible in Loki.
    const key = `${level}|${scope}|${msg}`
    const shippedForKey = perKeyShipped.get(key) ?? 0
    if (shippedForKey >= MAX_PER_KEY_PER_PAGE) return
    perKeyShipped.set(key, shippedForKey + 1)

    const query = currentQueryParams()
    const data: Record<string, unknown> = { table: query.table ?? null, kiosk: query.kiosk ?? null, query }

    if (shippedForKey + 1 === MAX_PER_KEY_PER_PAGE) {
      queue.push({
        level: 'error',
        scope: 'log-shipper',
        msg: `rate limit: repeats of the following line are now dropped for this page load: [${level}|${scope}] ${msg.slice(0, 300)}`,
        ts: Date.now(),
        pageUrl: window.location.href,
        domain: window.location.hostname,
        count: 1,
        data,
      })
    }
    queue.push({
      level,
      scope,
      msg,
      ts: Date.now(),
      pageUrl: window.location.href,
      domain: window.location.hostname,
      count: 1,
      data,
    })

    // Errors still ship promptly, but through the spaced flush — NEVER one POST per line.
    if (queue.length >= FLUSH_AT || level === 'error') requestFlush()
  } catch {
    // never let the shipper throw
  }
}

let consolePatched = false

/** Patch console.error/console.warn so both ship to Loki (originals still run first — the
 * console stays the local debugging surface). console.log/info/debug are deliberately left
 * alone; this kiosk only cares about warn/error volume. */
export function installConsoleErrorWarnShipping() {
  if (consolePatched || typeof window === 'undefined') return
  consolePatched = true
  const originalWarn = console.warn.bind(console)
  const originalError = console.error.bind(console)
  console.warn = (...args: unknown[]) => {
    originalWarn(...args)
    shipLog('warn', 'console', args)
  }
  console.error = (...args: unknown[]) => {
    originalError(...args)
    shipLog('error', 'console', args)
  }
}

// Round-22 (2026-08-31): "track everything" observability convention (DESIGN.md § Observability
// — Track Everything). Every notable recognition attempt/decision/outcome ships one compact
// structured 'info' line here so debugging works from the Loki eventstream instead of reading
// numbers off a live screen. Deliberately NOT deduped/aggregated by content the way warn/error
// repeats are (shipLog already handles identical-line aggregation via its `existing` lookup —
// that still applies here, it just rarely fires since `data` usually differs attempt to attempt).
// Fields are ids/scores/texts ONLY — never raw vectors or audio; keep payloads small.
export function shipEvent(scope: string, data: Record<string, unknown>) {
  try {
    shipLog('info', scope, [JSON.stringify(data)])
  } catch {
    // never let tracking break the kiosk
  }
}
